// ===================================================================
// EPSILLON LEADS - CENTRALISED CONTENT
// All copy, pricing, feature lists, FAQ, and comparison data.
// Update content here. Never touch components for copy changes.
// ===================================================================

export const BRAND = {
  name: "Epsillon Leads",
  parent: "Epsillon Media Pty Ltd",
  tagline: "Stop Losing Leads. Start Booking More Jobs.",
  email: "hello@epsillonmedia.com.au",
  abn: "XX XXX XXX XXX",
  url: "https://leads.epsillonmedia.com.au",
  signupPath: "/signup",
};

export const NAV_LINKS = [
  { label: "Home", href: "/" },
  { label: "Features", href: "/features" },
  { label: "Pricing", href: "/pricing" },
  { label: "Login", href: "/login" },
];

export const HERO = {
  headline: "Every Missed Call Is a Missed Job.",
  subheadline:
    "Epsillon Leads captures every enquiry, follows up instantly, and books jobs while you are on the tools. The all-in-one CRM built for Australian trades businesses.",
  cta: "Start Your 7-Day Free Trial",
  ctaSubtext: "Then $299/month. No lock-in. Cancel anytime.",
  ctaSecondary:
    "Other platforms charge $600+/month for the same features. You get everything for less than half.",
  trustBar: [
    "Australian Owned",
    "Built for Tradies",
    "Setup in Under 10 Minutes",
    "No Lock-In Contract",
  ],
};

export const PAIN_POINTS = {
  heading: "Sound Familiar?",
  cards: [
    {
      icon: "PhoneOff",
      title: "Missed Calls, Lost Jobs",
      body: "You are on a roof or under a house. The phone rings. You miss it. That lead calls your competitor instead. Gone forever.",
    },
    {
      icon: "Clock",
      title: "Leads Go Cold Because You Are Busy",
      body: "You get the enquiry at 2pm. You follow up at 7pm. They already booked someone else. Speed wins. You need a system that responds in seconds, not hours.",
    },
    {
      icon: "FileX",
      title: "No System to Track Anything",
      body: "Leads in your inbox. Jobs in your head. Follow-ups on sticky notes. You are running a business from memory and it is costing you money.",
    },
    {
      icon: "TrendingDown",
      title: "Paying for Ads With No Follow-Up",
      body: "You spend thousands on Google and Facebook ads but have no system to capture those leads and convert them. That is not a marketing problem. It is a follow-up problem.",
    },
  ],
};

export const SOLUTION = {
  heading: "One System. Every Lead. Zero Dropped Jobs.",
  body: "Epsillon Leads is a CRM and marketing automation platform built specifically for trades businesses. It captures every lead from your ads, website, Google, and referrals. It follows up instantly with AI-powered SMS and email. It books appointments and sends reminders. All from one dashboard you run from your phone.",
  steps: [
    {
      icon: "BellRing",
      label: "Lead Comes In",
      subtext: "From ads, Google, website, referrals",
    },
    {
      icon: "Zap",
      label: "AI Follows Up Instantly",
      subtext: "SMS and email within seconds",
    },
    {
      icon: "CalendarCheck",
      label: "Job Gets Booked",
      subtext: "Automated booking and reminders",
    },
  ],
};

export const VALUE_STACK = {
  heading: "Here Is Everything You Get for $299/Month",
  subheading:
    "We added up the cost of every tool Epsillon Leads replaces. The total will make you angry at what you have been paying.",
  items: [
    {
      name: "AI-Powered Lead Follow-Up",
      benefit:
        "Every lead gets an instant SMS and email. Your AI assistant qualifies and books them.",
      value: "$200/month",
    },
    {
      name: "Missed Call Text-Back",
      benefit:
        "Automatic text sent when you miss a call. Never lose a lead to voicemail again.",
      value: "$50/month",
    },
    {
      name: "CRM and Contact Management",
      benefit:
        "Unlimited contacts. Full history. Notes, tags, and pipeline tracking.",
      value: "$99/month",
    },
    {
      name: "Online Booking Calendar",
      benefit:
        "Leads book directly into your schedule. Automated reminders reduce no-shows.",
      value: "$40/month",
    },
    {
      name: "Email and SMS Marketing",
      benefit:
        "Send campaigns, promotions, and follow-ups. Built-in templates for trades.",
      value: "$80/month",
    },
    {
      name: "Review Request Automation",
      benefit:
        "Automatically ask for Google reviews after every completed job.",
      value: "$50/month",
    },
    {
      name: "Landing Page and Funnel Builder",
      benefit:
        "Build lead capture pages without a developer. Drag and drop.",
      value: "$100/month",
    },
    {
      name: "Workflow Automations",
      benefit:
        "Build custom automations for follow-ups, reminders, status updates. Set it and forget it.",
      value: "$75/month",
    },
    {
      name: "Reporting Dashboard",
      benefit:
        "See where your leads come from, what converts, and where you are losing money.",
      value: "$50/month",
    },
    {
      name: "Mobile App (iOS and Android)",
      benefit:
        "Manage your entire business from your phone on the job site.",
      value: "$30/month",
    },
    {
      name: "Pre-Built Trades Templates",
      benefit:
        "Pipelines, automations, messages, and landing pages built for your trade. Ready to go.",
      value: "$200 one-time",
      isOneTime: true,
    },
  ],
  totalValue: "$974+/month",
  yourPrice: "$299/month",
  savingsText:
    "That is a 70% saving. And unlike the other guys, there is no lock-in contract.",
  cta: "Start Your 7-Day Free Trial",
  ctaSubtext:
    "Full access. No restrictions. Cancel before day 7 and pay nothing.",
};

export const FEATURES = {
  heading: "Everything You Need. Nothing You Don't.",
  items: [
    {
      icon: "MessageSquareLightning",
      title: "AI Lead Follow-Up",
      description:
        "Instant SMS and email response to every new lead. AI qualifies and books appointments.",
    },
    {
      icon: "PhoneForwarded",
      title: "Missed Call Text-Back",
      description:
        "Miss a call? Automatic text goes out in seconds. Keeps the lead warm while you work.",
    },
    {
      icon: "CalendarDays",
      title: "Online Booking",
      description:
        "Leads book directly into your calendar. Automated reminders cut no-shows.",
    },
    {
      icon: "GitPullRequestDraft",
      title: "Visual Job Pipeline",
      description:
        "See every lead, quote, and job in one view. Drag and drop to track progress.",
    },
    {
      icon: "Star",
      title: "Review Automation",
      description:
        "Request Google reviews automatically after every job. Build your reputation on autopilot.",
    },
    {
      icon: "Smartphone",
      title: "Mobile App",
      description:
        "Manage leads, messages, pipeline, and bookings from your phone. Works anywhere with reception.",
    },
  ],
};

export const COMPARISON = {
  heading: "How We Stack Up Against the Competition",
  subheading:
    "Most CRM platforms charge $600+ AUD/month for their basic plan. Here is what you get with Epsillon Leads at less than half the price.",
  usLabel: "Epsillon Leads ($299/mo)",
  themLabel: "Other CRM Platforms (from $600+/mo)",
  rows: [
    { feature: "AI Lead Follow-Up (SMS + Email)", us: true, them: "Add-on / Higher tier" },
    { feature: "Missed Call Text-Back", us: true, them: true },
    { feature: "CRM with Unlimited Contacts", us: true, them: "Limited" },
    { feature: "Online Booking Calendar", us: true, them: false },
    { feature: "Landing Page Builder", us: true, them: false },
    { feature: "Email Marketing", us: true, them: "Higher tiers only" },
    { feature: "SMS Marketing", us: true, them: true },
    { feature: "Workflow Automations", us: true, them: "Basic / Add-on" },
    { feature: "Review Request Automation", us: true, them: true },
    { feature: "Visual Pipeline Management", us: true, them: false },
    { feature: "Reporting Dashboard", us: true, them: "Higher tiers only" },
    { feature: "Mobile App", us: true, them: true },
    { feature: "Pre-Built Trades Templates", us: true, them: false },
    { feature: "No Lock-In Contract", us: true, them: "12-month minimum" },
    { feature: "Export Your Data Anytime", us: true, them: "Restricted" },
    { feature: "Australian-Owned", us: true, them: false },
    { feature: "Price (AUD)", us: "$299/month", them: "$600+/month" },
  ],
  callout:
    "Enterprise CRM platforms are built for large businesses with large budgets. Epsillon Leads is built for tradies who want the same tools without the enterprise price tag or the lock-in contract.",
  cta: "Start Your 7-Day Free Trial",
};

// Placeholder numbers - update with real data
export const SOCIAL_PROOF = {
  stats: [
    { value: "500+", label: "Trades Businesses" },
    { value: "50,000+", label: "Leads Captured" },
    { value: "3x", label: "Average Increase in Booked Jobs" },
  ],
  testimonials: [
    {
      quote:
        "Before Epsillon Leads I was losing half my enquiries. Now every lead gets followed up within 30 seconds. My bookings have doubled.",
      name: "[Placeholder Name]",
      trade: "Electrician, Perth",
    },
    {
      quote:
        "I used to spend my evenings returning calls and sending quotes. Now the system handles it. I finish work and I am done.",
      name: "[Placeholder Name]",
      trade: "Plumber, Melbourne",
    },
    {
      quote:
        "The missed call text-back alone has paid for the system ten times over. I was losing 3 to 4 leads a week before this.",
      name: "[Placeholder Name]",
      trade: "Builder, Brisbane",
    },
  ],
};

export const PRICING = {
  heading: "Simple Pricing. No Surprises.",
  badge: "All-Inclusive",
  planName: "Epsillon Leads Pro",
  price: "$299",
  priceSuffix: "/month AUD",
  trial: "Start with a 7-day free trial",
  features: [
    "AI Lead Follow-Up (SMS + Email)",
    "Missed Call Text-Back",
    "CRM with Unlimited Contacts",
    "Online Booking Calendar",
    "Visual Job Pipeline",
    "Review Request Automation",
    "Email and SMS Marketing",
    "Landing Page and Funnel Builder",
    "Workflow Automations",
    "Reporting Dashboard",
    "Mobile App (iOS + Android)",
    "Pre-Built Trades Templates",
    "Ongoing Updates and New Features",
  ],
  cta: "Start Your 7-Day Free Trial",
  ctaSubtext: "Cancel anytime. No lock-in. No hidden fees.",
  setupCallout:
    "Want us to set everything up for you? Our Professional Setup service ($499 one-time) configures your system, imports your contacts, and gets you live within 48 hours. Available after you start your trial.",
};

export const FAQ_DATA = {
  heading: "Common Questions",
  items: [
    {
      question: "Do I need to be tech savvy?",
      answer:
        "No. If you can send a text message, you can use Epsillon Leads. The pre-built trades templates mean most of the setup is done before you even log in.",
    },
    {
      question: "How long does setup take?",
      answer:
        "Most trades businesses are running within 30 minutes. Your pipeline, follow-up messages, and automations are pre-loaded. If you want hands-off setup, our Professional Setup service ($499) gets everything configured within 48 hours.",
    },
    {
      question: "Does it work with my existing website?",
      answer:
        "Yes. Embed lead capture forms and booking widgets on any website. It works with your Google Business Profile, Facebook page, and any ad platform.",
    },
    {
      question: "What trades does it work for?",
      answer:
        "Electricians, plumbers, builders, renovators, HVAC technicians, painters, landscapers, cabinet makers, and any service-based trade. The templates are built specifically for trades businesses.",
    },
    {
      question: "Can I use it on my phone?",
      answer:
        "Yes. iOS and Android apps. Manage leads, respond to messages, check your pipeline, and book jobs from anywhere you have reception.",
    },
    {
      question: "What happens during the 7-day trial?",
      answer:
        "Full access to everything. No restrictions. Your trades templates are loaded automatically. Cancel before day 7 and you will not be charged.",
    },
    {
      question: "Is there a lock-in contract?",
      answer:
        "No. Month-to-month. Cancel anytime with no penalties. No exit fees. No 'case by case' data export requests. Your data is yours.",
    },
    {
      question: "How is this different from other CRM platforms?",
      answer:
        "Most CRM platforms built for trades start at $600+ AUD/month, lock you into 12-month contracts, and charge extra for features like landing pages and advanced automations. Epsillon Leads includes everything for $299/month with no lock-in. You get AI follow-up, missed call text-back, review automation, landing pages, visual pipeline management, and pre-built trades templates. All included. No add-ons.",
    },
    {
      question: "How is this different from ServiceM8 or Tradify?",
      answer:
        "ServiceM8 and Tradify are job management tools. They help you manage jobs you have already won. Epsillon Leads is a marketing and lead management system. It helps you win more jobs by capturing leads, following up instantly, and booking appointments before your competitors can respond. They manage work. We generate it.",
    },
  ],
};

export const FINAL_CTA = {
  heading: "Your Competitors Are Following Up Faster Than You.",
  subheading:
    "Every hour without a system is another lead lost to someone who picked up the phone first. Or someone whose system replied in 30 seconds.",
  cta: "Start Your 7-Day Free Trial",
  ctaSubtext: "$299/month after trial. Cancel anytime. No lock-in.",
  belowText:
    "Takes 3 minutes to sign up. Your trades templates are loaded automatically.",
};

export const FOOTER = {
  brand: "Epsillon Leads is a product of Epsillon Media Pty Ltd",
  abn: "ABN: XX XXX XXX XXX",
  navLinks: [
    { label: "Home", href: "/" },
    { label: "Features", href: "/features" },
    { label: "Pricing", href: "/pricing" },
    { label: "Login", href: "/login" },
  ],
  links: [
    { label: "Privacy Policy", href: "#" },
    { label: "Terms of Service", href: "#" },
  ],
  email: "hello@epsillonmedia.com.au",
  phone: "[phone placeholder]",
  copyright: "2025 Epsillon Media Pty Ltd. All rights reserved.",
  australianBadge: "Australian Owned and Operated",
};


// ===================================================================
// FEATURES PAGE (Page 2)
// ===================================================================

export const FEATURES_PAGE = {
  hero: {
    headline: "Every Feature Your Trades Business Needs. Nothing It Doesn't.",
    subheadline:
      "One platform to capture leads, follow up instantly, book jobs, get reviews, and track every dollar. Built for tradies, not tech companies.",
    cta: "Start Your 7-Day Free Trial",
    ctaSubtext: "No lock-in contracts. Cancel anytime. $299/month after trial.",
    trustItems: [
      { text: "Australian owned and supported", icon: "Flag" },
      { text: "Set up in under 10 minutes", icon: "Timer" },
      { text: "Used by 50+ trades businesses", icon: "Users" },
    ],
  },
  sections: [
    {
      id: "lead-capture",
      label: "LEAD CAPTURE",
      heading: "Stop Losing Leads From Your Website",
      body: "Every visitor to your website is a potential job. Epsillon Leads gives you ready-made forms, landing pages, and a website chat widget that captures enquiries the moment they land. No more leads slipping through the cracks because your contact page was buried three clicks deep.",
      bullets: [
        "Drag-and-drop landing page builder (no coding required)",
        "Embeddable forms for your existing website",
        "Live website chat widget that captures details 24/7",
      ],
      placeholder: "Screenshot: Lead capture form builder interface",
      imageLeft: false,
      bgLight: false,
    },
    {
      id: "ai-lead-nurture",
      label: "AI LEAD NURTURE",
      heading: "Respond to Every Lead in Under 60 Seconds. Automatically.",
      body: "Speed wins jobs. When a new lead comes in, Epsillon Leads sends an instant SMS and email response on your behalf. The built-in AI continues the conversation, answers common questions, and qualifies the lead before you even pick up the phone. You look responsive and professional without lifting a finger.",
      bullets: [
        "Instant SMS and email response to new enquiries",
        "AI-powered conversation handles initial qualification",
        "Hands-free follow-up sequences until the lead books or declines",
      ],
      placeholder: "Screenshot: AI conversation thread with a lead",
      imageLeft: true,
      bgLight: true,
    },
    {
      id: "missed-call-textback",
      label: "MISSED CALL TEXT-BACK",
      heading: "Miss a Call. Win the Job Anyway.",
      body: "You are on a roof. You are under a house. You are driving between jobs. You miss calls every day, and every missed call is money walking to a competitor. Epsillon Leads sends an automatic text message the moment you miss a call, letting the customer know you saw it and will be in touch. Most of them text back instead of calling someone else.",
      bullets: [
        "Automatic text sent within seconds of a missed call",
        "Customisable message templates for your business",
        "Keeps the lead warm until you are free to call back",
      ],
      placeholder: "Screenshot: Missed call text-back notification on mobile",
      imageLeft: false,
      bgLight: false,
    },
    {
      id: "online-booking",
      label: "ONLINE BOOKING",
      heading: "Let Customers Book Jobs While You Work",
      body: "No more phone tag. No more back-and-forth texts trying to lock in a time. Epsillon Leads gives your customers a booking page where they choose a time that works for both of you. Automated reminders go out before the appointment, cutting no-shows and saving you the hassle of manual confirmations.",
      bullets: [
        "Branded online booking page with your availability",
        "Syncs with Google Calendar so nothing double-books",
        "Automated SMS and email reminders reduce no-shows",
      ],
      placeholder: "Screenshot: Online booking calendar interface",
      imageLeft: true,
      bgLight: true,
    },
    {
      id: "pipeline-management",
      label: "PIPELINE MANAGEMENT",
      heading: "See Every Job From First Call to Final Invoice",
      body: "Whiteboards and spreadsheets do not scale. Epsillon Leads gives you a visual pipeline where every lead, quote, and active job sits in a clear stage. Drag and drop jobs between stages. Know exactly what is pending, what is booked, and what needs follow-up. Nothing falls through the cracks.",
      bullets: [
        "Visual drag-and-drop pipeline board",
        "Custom stages for your workflow (Lead, Quoted, Booked, Complete)",
        "Quick-view contact details and notes on every card",
      ],
      placeholder: "Screenshot: Pipeline board with job cards in different stages",
      imageLeft: false,
      bgLight: false,
    },
    {
      id: "review-generation",
      label: "REVIEW GENERATION",
      heading: "Turn Finished Jobs Into 5-Star Reviews on Autopilot",
      body: "Google reviews are the number one factor when homeowners pick a tradie. Epsillon Leads sends an automated review request via SMS after every completed job. One tap takes the customer straight to your Google profile. More reviews mean more trust, more clicks, and more jobs from Google search.",
      bullets: [
        "Automated SMS review request after job completion",
        "Direct link to your Google Business Profile",
        "Track review count and average rating inside the dashboard",
      ],
      placeholder: "Screenshot: Review request SMS and Google review prompt",
      imageLeft: true,
      bgLight: true,
    },
    {
      id: "reporting",
      label: "REPORTING",
      heading: "Know Exactly Where Your Jobs Come From",
      body: "Stop guessing which marketing is working. Epsillon Leads tracks every lead source, conversion rate, and revenue figure in one dashboard. See which channels bring the best leads, how fast your team responds, and how much revenue your pipeline holds. Make decisions based on numbers, not gut feel.",
      bullets: [
        "Lead source tracking (Google, Facebook, referrals, website)",
        "Conversion rate reporting at every pipeline stage",
        "Revenue tracking tied to lead source for true ROI",
      ],
      placeholder: "Screenshot: Reporting dashboard with charts and metrics",
      imageLeft: false,
      bgLight: false,
    },
  ],
  mobileApp: {
    label: "MOBILE APP",
    heading: "Run Your Business From Your Back Pocket",
    body: "Everything in Epsillon Leads works on your phone. Check new leads between jobs. Respond to enquiries from the ute. Move jobs through your pipeline on site. The mobile app gives you full control without being chained to a desk.",
    bullets: [
      "Full CRM access on iOS and Android",
      "Push notifications for new leads and messages",
      "Respond to leads, book jobs, and update pipelines on the go",
    ],
    phonePlaceholders: [
      "Screenshot: Mobile app dashboard view",
      "Screenshot: Mobile app conversation view",
    ],
  },
  cta: {
    heading: "Ready to Stop Losing Leads?",
    body: "Start your 7-day free trial today. Full access to every feature. No lock-in contracts. If it does not work for your business, cancel before the trial ends and pay nothing.",
    ctaButton: "Start Your Free Trial Now",
    secondary: "Questions? Call us on [phone placeholder] or chat with our team.",
  },
};


// ===================================================================
// PRICING PAGE (Page 3)
// ===================================================================

export const PRICING_PAGE = {
  hero: {
    headline: "One Plan. Everything Included. No Surprises.",
    subheadline:
      "$299/month platform fee. SMS and calls billed on usage. Every feature included.",
  },
  card: {
    label: "PLATFORM FEE",
    price: "$299",
    priceSuffix: "/month",
    currency: "AUD inc GST.",
    gst: "SMS and calls billed on usage.",
    featuresCol1: [
      "Unlimited users",
      "Unlimited contacts",
      "Lead capture forms",
      "Landing page builder",
      "Website chat widget",
      "AI lead nurture (SMS and email)",
      "Missed call text-back",
      "Online booking calendar",
    ],
    featuresCol2: [
      "Automated reminders",
      "Pipeline management",
      "Review generation",
      "Reporting dashboard",
      "Mobile app (iOS and Android)",
      "Email and SMS templates included",
      "Google Calendar sync",
      "Australian-based support",
    ],
    sellingPoints: [
      {
        icon: "CalendarCheck",
        text: "7-day free trial. Full access to everything.",
      },
      {
        icon: "LockOpen",
        text: "No lock-in contracts. Cancel anytime.",
      },
      {
        icon: "UsersRound",
        text: "No per-user fees. Add your whole team.",
      },
    ],
    cta: "Start Your 7-Day Free Trial",
    ctaSubtext: "Credit card required. Cancel before day 7 and pay nothing.",
  },
  usagePricing: {
    heading: "SMS, Calls, and Phone Numbers",
    subheading:
      "Billed based on what you use each month. No minimum spend. No bundled packages you do not need. All prices include GST.",
    items: [
      { item: "Outbound SMS", rate: "15.5c per message", notes: "Automated and manual texts" },
      { item: "Inbound SMS", rate: "3.5c per message", notes: "Replies from your customers" },
      { item: "Outbound call (mobile)", rate: "22.5c per minute", notes: "Calls to mobile numbers" },
      { item: "Outbound call (landline)", rate: "4.5c per minute", notes: "Calls to landlines" },
      { item: "Inbound call (local number)", rate: "2.6c per minute", notes: "Calls to your business number" },
      { item: "Local phone number", rate: "$9.00 per month", notes: "Your dedicated business number" },
      { item: "Toll-free number", rate: "$9.00 per month", notes: "Optional 1300/1800 number" },
    ],
    callout: {
      heading: "What Does a Typical Tradie Spend?",
      body: "Most trades businesses send 200 to 500 SMS per month and make 50 to 100 calls. A typical monthly usage bill is $30 to $80. Combined with your $299 platform fee, most clients pay $330 to $380 total per month for everything.",
    },
  },
  valueComparison: {
    heading: "What You Would Pay for All This Separately",
    subheading:
      "Most trades businesses piece together 5 or 6 different tools to do what Epsillon Leads does in one platform. Here is what those tools cost on their own.",
    items: [
      { tool: "CRM Platform", desc: "Store contacts, track leads, manage pipeline", cost: "$99/month" },
      { tool: "SMS Marketing Tool", desc: "Send texts to leads and customers", cost: "$59/month" },
      { tool: "Email Marketing Platform", desc: "Automated email sequences and campaigns", cost: "$49/month" },
      { tool: "Online Booking Software", desc: "Calendar booking and automated reminders", cost: "$39/month" },
      { tool: "Review Management Tool", desc: "Automated review requests and monitoring", cost: "$49/month" },
      { tool: "AI Chatbot / Lead Nurture", desc: "Automated lead qualification and follow-up", cost: "$99/month" },
      { tool: "Landing Page Builder", desc: "Build lead capture pages without a developer", cost: "$49/month" },
      { tool: "Missed Call Text-Back", desc: "Automatic text when you miss a call", cost: "$29/month" },
      { tool: "Reporting Dashboard", desc: "Track lead sources, conversions, revenue", cost: "$39/month" },
      { tool: "Mobile App Access", desc: "Manage your business from your phone", cost: "$19/month" },
    ],
    total: "$630+/month",
    epsillonPrice: "$299/month + typical usage of $30 to $80",
    epsillonTotal: "Total: around $330 to $380/month",
    savings: "$250+",
  },
  competitorComparison: {
    heading: "How Epsillon Leads Compares",
    subheading:
      "Other CRM platforms charge $600+ per month, lock you into 12-month contracts, and nickel-and-dime you with add-on fees. We do not.",
    usLabel: "Epsillon Leads",
    themLabel: "Other CRM Platforms ($600+/mo)",
    rows: [
      { feature: "Monthly price", us: "$299/month", them: "$600+/month" },
      { feature: "Lock-in contract", us: "No. Cancel anytime.", them: "12-month minimum" },
      { feature: "Per-user fees", us: "None. Unlimited users.", them: "$20 to $50 per user" },
      { feature: "Setup fees", us: "None", them: "$500 to $2,000" },
      { feature: "AI lead nurture", us: "Included", them: "$99+/month add-on" },
      { feature: "SMS sending", us: "Included", them: "Pay per message" },
      { feature: "Missed call text-back", us: "Included", them: "Not available or add-on" },
      { feature: "Review automation", us: "Included", them: "$49+/month add-on" },
      { feature: "Landing pages", us: "Included", them: "Limited or add-on" },
      { feature: "Online booking", us: "Included", them: "Add-on" },
      { feature: "Mobile app", us: "Full access", them: "Limited functionality" },
      { feature: "Australian support", us: "Yes. Local team.", them: "Overseas call centres" },
      { feature: "Data ownership", us: "You own your data. Export anytime.", them: "Restricted exports" },
    ],
  },
  professionalSetup: {
    label: "OPTIONAL ADD-ON",
    heading: "Want Us to Set It Up For You?",
    body: "Not everyone has time to configure a new platform. For a one-time fee of $499, our team will set up your entire Epsillon Leads account. Pipelines, automations, templates, and integrations. Done in 48 hours. Ready to use when you log in.",
    checklist: [
      "Custom pipeline stages for your business",
      "SMS and email templates configured",
      "Google Calendar integration",
      "Review automation setup",
      "Lead capture form built and tested",
      "30-minute walkthrough call with our team",
    ],
    price: "$499",
    priceSuffix: "one-time",
    cta: "Add Professional Setup",
    ctaSubtext: "Available during your trial or anytime after.",
  },
  faq: {
    heading: "Common Questions About Pricing",
    items: [
      {
        question: "Is there really no lock-in contract?",
        answer:
          "No lock-in. No minimum term. You pay month to month. If Epsillon Leads is not right for your business, cancel from your dashboard before your next billing date. No phone calls, no hoops.",
      },
      {
        question: "What happens during the 7-day free trial?",
        answer:
          "You get full access to every feature for 7 days. No restrictions. SMS and call usage during the trial is billed at the standard rates shown above. If you cancel before day 7, your platform fee is not charged. You only pay for any SMS or calls you used.",
      },
      {
        question: "Are there any extra fees I should know about?",
        answer:
          "No setup fees. No per-user fees. No hidden charges. The $299/month covers the platform and every feature. SMS and call usage is billed separately at the rates shown on this page. The only other optional cost is the $499 Professional Setup if you want our team to configure everything for you.",
      },
      {
        question: "How does billing work?",
        answer:
          "Your credit card is charged $299 AUD (including GST) on the same date each month. You receive a tax invoice via email after every payment.",
      },
      {
        question: "Do I need to pay for each team member?",
        answer:
          "No. Unlimited users are included. Add your office manager, apprentice, or partner at no extra cost.",
      },
      {
        question: "What if I want to cancel?",
        answer:
          "Cancel anytime from your account settings. No cancellation fees. No phone calls required. Your account stays active until the end of your current billing period.",
      },
      {
        question: "Is my data safe? What happens if I leave?",
        answer:
          "Your data belongs to you. We do not hold it hostage. If you cancel, you get 30 days to export everything. Contacts, messages, pipeline data, all of it.",
      },
      {
        question: "Do you offer annual pricing?",
        answer:
          "Not at this stage. We keep it simple with month-to-month billing. No discounts needed when the price is already fair.",
      },
      {
        question: "How does SMS and call billing work?",
        answer:
          "Usage is tracked automatically inside the platform. You add credit to your account, and SMS and calls are deducted as you use them. All rates include GST. There is no minimum spend and no monthly commitment on usage.",
      },
      {
        question: "What is a typical monthly bill?",
        answer:
          "Most trades businesses pay $299 for the platform plus $30 to $80 in SMS and call usage. Your total monthly cost is typically $330 to $380 depending on how many leads you are following up.",
      },
      {
        question: "Is Epsillon Leads built on GoHighLevel?",
        answer:
          "Epsillon Leads is powered by the same infrastructure trusted by thousands of businesses worldwide. We have customised and configured it specifically for Australian trades businesses, with local support, local compliance, and workflows designed for how tradies operate.",
      },
    ],
  },
  guarantee: {
    heading: "Our Fair Go Guarantee",
    body: "Try Epsillon Leads for 7 days. Use every feature. If it is not the right fit for your business, cancel before your trial ends and pay nothing. No questions asked. No hard feelings. We are confident the platform speaks for itself.",
  },
  cta: {
    heading: "Ready to Run Your Business on One Platform?",
    body: "Start your 7-day free trial. $299/month after trial. SMS and calls billed on usage. No lock-in. Cancel anytime.",
    ctaButton: "Start Your Free Trial Now",
    secondary: "Questions? Call us on [phone placeholder] or email hello@epsillonmedia.com.au.",
  },
};
